import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateReceipt } from '@/hooks/useInventoryReceipts';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, Loader2, AlertCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr, arSA } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { InventoryReceipt } from '@/types/entities';

interface EditReceiptDialogProps {
  restaurantId: string;
  receipt: InventoryReceipt;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditReceiptDialog({
  restaurantId,
  receipt,
  open,
  onOpenChange,
}: EditReceiptDialogProps) {
  const { t, i18n } = useTranslation();
  const updateMutation = useUpdateReceipt(restaurantId);

  const [formData, setFormData] = useState({
    quantityReceived: receipt.quantityReceived,
    costPerUnit: receipt.costPerUnit,
    receiptDate: parseISO(receipt.receiptDate),
    supplier: receipt.supplier || '',
    invoiceNumber: receipt.invoiceNumber || '',
    notes: receipt.notes || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const locale = i18n.language === 'ar' ? arSA : fr;
  const isRTL = i18n.language === 'ar';

  // Reset form when receipt changes
  useEffect(() => {
    setFormData({
      quantityReceived: receipt.quantityReceived,
      costPerUnit: receipt.costPerUnit,
      receiptDate: parseISO(receipt.receiptDate),
      supplier: receipt.supplier || '',
      invoiceNumber: receipt.invoiceNumber || '',
      notes: receipt.notes || '',
    });
    setErrors({});
  }, [receipt]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.quantityReceived || formData.quantityReceived <= 0) {
      newErrors['quantityReceived'] = t('validation.required');
    }
    if (formData.costPerUnit === undefined || formData.costPerUnit < 0) {
      newErrors['costPerUnit'] = t('validation.required');
    }
    if (!formData.receiptDate) {
      newErrors['receiptDate'] = t('validation.required');
    }
    if (formData.receiptDate > new Date()) {
      newErrors['receiptDate'] = t('validation.dateCannotBeFuture');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    updateMutation.mutate(
      {
        receiptId: receipt.id,
        quantityReceived: formData.quantityReceived,
        unit: receipt.unit, // Include the unit from the receipt
        costPerUnit: formData.costPerUnit,
        receiptDate: formData.receiptDate,
        supplier: formData.supplier || null,
        invoiceNumber: formData.invoiceNumber || null,
        notes: formData.notes || null,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      }
    );
  };

  const totalCost = formData.quantityReceived * formData.costPerUnit;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('inventory.editReceipt.title')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Ingredient display (read-only) */}
          <div>
            <Label>{t('inventory.addReceiptDialog.ingredient')}</Label>
            <div className="mt-1 p-2 bg-muted rounded-md text-sm">
              {i18n.language === 'ar' ? receipt.ingredient?.nameAr : receipt.ingredient?.nameFr}
            </div>
          </div>

          {/* Quantity */}
          <div>
            <Label htmlFor="quantity">{t('inventory.addReceiptDialog.quantity')}</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="quantity"
                type="number"
                step="0.001"
                value={formData.quantityReceived === 0 ? '' : formData.quantityReceived}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                  setFormData({ ...formData, quantityReceived: isNaN(value) ? 0 : value });
                }}
                placeholder="0.000"
                className={errors['quantityReceived'] ? 'border-red-500' : ''}
              />
              <div className="flex items-center px-3 bg-muted rounded-md text-sm min-w-[60px]">
                {receipt.unit}
              </div>
            </div>
            {errors['quantityReceived'] && (
              <p className="text-xs text-red-500 mt-1">{errors['quantityReceived']}</p>
            )}
          </div>

          {/* Cost per unit */}
          <div>
            <Label htmlFor="cost">{t('inventory.addReceiptDialog.costPerUnit')}</Label>
            <Input
              id="cost"
              type="number"
              step="0.01"
              value={formData.costPerUnit === 0 ? '' : formData.costPerUnit}
              onChange={(e) => {
                const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                setFormData({ ...formData, costPerUnit: isNaN(value) ? 0 : value });
              }}
              placeholder="0.00"
              className={errors['costPerUnit'] ? 'border-red-500' : ''}
            />
            {errors['costPerUnit'] && (
              <p className="text-xs text-red-500 mt-1">{errors['costPerUnit']}</p>
            )}
          </div>

          {/* Receipt Date */}
          <div>
            <Label>{t('inventory.addReceiptDialog.receiptDate')}</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal mt-1',
                    !formData.receiptDate && 'text-muted-foreground',
                    errors['receiptDate'] && 'border-red-500'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.receiptDate ? (
                    format(formData.receiptDate, 'PPP', { locale })
                  ) : (
                    <span>{t('inventory.addReceiptDialog.selectDate')}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align={isRTL ? 'end' : 'start'}>
                <Calendar
                  mode="single"
                  selected={formData.receiptDate}
                  onSelect={(date) => date && setFormData({ ...formData, receiptDate: date })}
                  disabled={(date) => date > new Date()}
                  locale={locale}
                  dir={isRTL ? 'rtl' : 'ltr'}
                />
              </PopoverContent>
            </Popover>
            {errors['receiptDate'] && (
              <p className="text-xs text-red-500 mt-1">{errors['receiptDate']}</p>
            )}
          </div>

          {/* Supplier */}
          <div>
            <Label htmlFor="supplier">{t('inventory.addReceiptDialog.supplier')}</Label>
            <Input
              id="supplier"
              value={formData.supplier}
              onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
            />
          </div>

          {/* Invoice Number */}
          <div>
            <Label htmlFor="invoice">{t('inventory.addReceiptDialog.invoiceNumber')}</Label>
            <Input
              id="invoice"
              value={formData.invoiceNumber}
              onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
            />
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">{t('inventory.addReceiptDialog.notes')}</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
            />
          </div>

          {/* Warning and Summary */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="text-sm">{t('inventory.editReceipt.warning')}</div>
              <div className="text-sm font-semibold mt-2">
                {t('inventory.addReceiptDialog.totalCost')}: {totalCost.toFixed(2)}{' '}
                {t('common.currency')}
              </div>
            </AlertDescription>
          </Alert>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
